import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const section = document.querySelector('[data-eyn]');
const root    = document.querySelector('[data-eyn-3d]');
if (!section || !root) throw new Error('eyn-stick-3d: root missing');

const MODEL_URLS = [
  root.dataset.model,
  root.dataset.model2,
  root.dataset.model3,
].filter(Boolean);
if (!MODEL_URLS.length) throw new Error('eyn-stick-3d: no model URLs');

const BACKPANEL_URLS = [
  root.dataset.backpanel,
  root.dataset.backpanel2,
  root.dataset.backpanel3,
].filter(Boolean);

/* ── constants ─────────────────────────────────────────── */
const STICK_HEIGHT = 1.0;
const BASE_TILT_Z  = -0.58;
const AUTO_SPIN    = 0.35;
const DRAG_SENS    = 0.011;
const POINTER_SPIN = 0.9;
const MAX_TILT     = 0.3;
const BOB_AMP      = 0.03;
const BOB_SPEED    = 0.9;
const BOB_PHASES   = [0, Math.PI * 0.65, Math.PI * 1.3];
/* CSS fade: root fades to 0 → model swaps → root fades to 1 */
const SWAP_MS    = 180;
const SWIPE_VEL  = 0.35; // px/ms — horizontal velocity to trigger swipe nav
const SWIPE_DIST = 35;   // px  — minimum horizontal travel to trigger swipe nav

/* spin-to-front on flavor change (disable with data-face-front="false") */
const FACE_FRONT     = root.dataset.faceFront !== 'false';
const FRONT_ANGLE    = 0;    // spinY where the label faces the camera
const FACE_FRONT_DUR  = 0.9;  // seconds for the settle spin
const FRONT_HOLD_SECS = 1.6;  // pause facing front before auto-spin resumes
const TWO_PI         = Math.PI * 2;

const lerp  = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/* ── renderer ─────────────────────────────────────────── */
function getSize() {
  const r = root.getBoundingClientRect();
  return { width: Math.max(1, r.width), height: Math.max(1, r.height) };
}
const { width: W0, height: H0 } = getSize();
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(W0, H0);
renderer.outputColorSpace    = THREE.SRGBColorSpace;
renderer.toneMapping         = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
root.appendChild(renderer.domElement);

/* ── scene ────────────────────────────────────────────── */
/* camera distance ("zoom") per breakpoint — smaller = closer */
const CAM_Z_DESKTOP = 2.7;
const CAM_Z_MOBILE  = 2;
const mobileMQ = window.matchMedia('(max-width: 768px)');

const scene  = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(32, W0 / H0, 0.01, 50);
camera.lookAt(0, 0, 0);

function applyCameraZoom() {
  camera.position.set(0, 0, mobileMQ.matches ? CAM_Z_MOBILE : CAM_Z_DESKTOP);
}
applyCameraZoom();
mobileMQ.addEventListener('change', applyCameraZoom);

const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

scene.add(new THREE.HemisphereLight(0xffffff, 0xf0e4e0, 0.4));
const key  = new THREE.DirectionalLight(0xfff4e2, 2.2); key.position.set( 2.0, 3.0, 2.6); scene.add(key);
const fill = new THREE.DirectionalLight(0xd8e8ff, 0.6); fill.position.set(-2.4, 0.8, 1.8); scene.add(fill);
const rim  = new THREE.DirectionalLight(0xffffff, 1.3); rim.position.set(-0.6, 2.0,-2.4);  scene.add(rim);

/* spinGroup sits inside tiltGroup */
const spinGroup = new THREE.Group();
const tiltGroup = new THREE.Group();
tiltGroup.add(spinGroup);
tiltGroup.rotation.z = BASE_TILT_Z;
scene.add(tiltGroup);

/* ── model helpers ────────────────────────────────────── */
function fitModel(obj, targetH) {
  const box  = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  obj.scale.setScalar(targetH / Math.max(size.y, 0.001));
  box.setFromObject(obj);
  obj.position.sub(box.getCenter(new THREE.Vector3()));
}

/* ── flavor navigation ────────────────────────────────── */
/* models[i] = THREE.Object3D for index i, or null if that GLB failed to load */
let models        = [];
const wrappers    = [];
let activeIdx     = 0;
let transitioning = false;
let spinTween     = null; // { from, to, t } — active spin-to-front animation
let frontHold     = 0;    // seconds remaining to hold the front-facing pose
let ptrInfluence  = 1;    // smoothed 0..1 — dampens cursor-follow during front pose

/* Spin forward (auto-spin direction) to the next front-facing angle */
function startFaceFrontTween() {
  let target = Math.ceil((spinY - FRONT_ANGLE) / TWO_PI) * TWO_PI + FRONT_ANGLE;
  /* guarantee a visible swing — at least half a turn */
  if (target - spinY < Math.PI * 0.5) target += TWO_PI;
  spinTween = { from: spinY, to: target, t: 0 };
}

function updateDots(idx) {
  section.querySelectorAll('[data-eyn-dot]').forEach((d, i) => {
    d.classList.toggle('is-active', i === idx);
  });
}

function goTo(idx) {
  if (transitioning || idx === activeIdx || !models[idx]) return;

  transitioning    = true;
  const from       = activeIdx;
  activeIdx        = idx;
  updateDots(idx);

  /*
   * CSS fade-out on `root`, swap model visibility at opacity 0,
   * then fade-in. No THREE.js material transparency involved.
   */
  root.style.transition = `opacity ${SWAP_MS}ms ease`;
  root.style.opacity    = '0';

  setTimeout(() => {
    if (models[from]) models[from].visible = false;
    models[idx].visible = true;
    root.style.opacity  = '1';
    if (FACE_FRONT) startFaceFrontTween();

    setTimeout(() => {
      root.style.transition = '';
      root.style.opacity    = '';   /* let .is-ready CSS rule take over */
      transitioning         = false;
    }, SWAP_MS);
  }, SWAP_MS);
}

function navigate(dir) {
  const n = models.length;
  if (!n) return;
  /* step in direction, skip any null (failed) slots */
  let idx = (activeIdx + dir + n) % n;
  let tries = 0;
  while (!models[idx] && tries++ < n) idx = (idx + dir + n) % n;
  goTo(idx);
}

window.eynNavigate = navigate;

/* ── pointer / drag ───────────────────────────────────── */
const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
const drag    = { active: false, pid: null, startX: 0, startTime: 0, startSpinY: 0, moved: false };
let spinY  = -0.4;
let inView = true;

function setPtr(cx, cy) {
  const r = section.getBoundingClientRect();
  pointer.tx = clamp(((cx - r.left) / r.width)  * 2 - 1, -1, 1);
  pointer.ty = clamp(-(((cy - r.top) / r.height) * 2 - 1), -1, 1);
}
section.addEventListener('pointermove',  (e) => { if (!drag.active) setPtr(e.clientX, e.clientY); });
section.addEventListener('pointerleave', ()  => { if (!drag.active) { pointer.tx = 0; pointer.ty = 0; } });

const canvas = renderer.domElement;
canvas.addEventListener('pointerdown', (e) => {
  if (e.button) return;
  spinTween = null; frontHold = 0; /* user grabbed the stick — cancel settle spin */
  drag.active = true; drag.pid = e.pointerId; drag.moved = false;
  drag.startX = e.clientX; drag.startTime = Date.now(); drag.startSpinY = spinY;
  root.classList.add('is-dragging');
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener('pointermove', (e) => {
  if (!drag.active || e.pointerId !== drag.pid) return;
  const dx = e.clientX - drag.startX;
  if (Math.abs(dx) > 5) drag.moved = true;
  spinY = drag.startSpinY + dx * DRAG_SENS;
});
const endDrag = (e) => {
  if (!drag.active || e.pointerId !== drag.pid) return;

  const dx    = e.clientX - drag.startX;
  const dt    = Math.max(1, Date.now() - drag.startTime);
  const vel   = dx / dt; // px/ms
  const moved = drag.moved;

  drag.active = false; drag.pid = null; drag.moved = false;
  root.classList.remove('is-dragging');
  if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);

  if (!moved) {
    /* tap → open the active flavor's back panel */
    if (typeof window.eynOpenLightbox === 'function') {
      window.eynOpenLightbox(BACKPANEL_URLS[activeIdx] || BACKPANEL_URLS[0]);
    }
  } else if (Math.abs(vel) >= SWIPE_VEL && Math.abs(dx) >= SWIPE_DIST) {
    /* fast horizontal swipe → navigate (undo spin drift) */
    spinY = drag.startSpinY;
    navigate(vel > 0 ? -1 : 1);
  }
  /* slow drag → spin stays applied via pointermove */
};
canvas.addEventListener('pointerup',     endDrag);
canvas.addEventListener('pointercancel', endDrag);

/* ── arrow + dot buttons ──────────────────────────────── */
const prevBtn = section.querySelector('[data-eyn-prev]');
const nextBtn = section.querySelector('[data-eyn-next]');
if (prevBtn) prevBtn.addEventListener('click', () => navigate(-1));
if (nextBtn) nextBtn.addEventListener('click', () => navigate( 1));
section.querySelectorAll('[data-eyn-dot]').forEach(dot => {
  dot.addEventListener('click', () => goTo(parseInt(dot.dataset.eynDot, 10)));
});

if ('IntersectionObserver' in window) {
  new IntersectionObserver(
    (es) => { inView = es[0].isIntersecting; },
    { rootMargin: '80px' }
  ).observe(root);
}

/* ── animation ────────────────────────────────────────── */
const clock = new THREE.Clock();

function animate() {
  requestAnimationFrame(animate);
  if (!inView) return;

  const delta   = Math.min(clock.getDelta(), 0.05);
  const elapsed = clock.getElapsedTime();
  const { width, height } = getSize();
  const pr = renderer.getPixelRatio();
  if (canvas.width !== Math.floor(width * pr) || canvas.height !== Math.floor(height * pr)) {
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  }

  pointer.x = lerp(pointer.x, pointer.tx, 0.08);
  pointer.y = lerp(pointer.y, pointer.ty, 0.08);

  if (spinTween) {
    /* ease-out spin toward the front-facing angle */
    spinTween.t = Math.min(1, spinTween.t + delta / FACE_FRONT_DUR);
    const eased = 1 - Math.pow(1 - spinTween.t, 3);
    spinY = lerp(spinTween.from, spinTween.to, eased);
    if (spinTween.t >= 1) { spinTween = null; frontHold = FRONT_HOLD_SECS; }
  } else if (frontHold > 0) {
    frontHold -= delta; /* hold the front pose so the label is readable */
  } else if (!drag.active) {
    spinY += AUTO_SPIN * delta;
  }

  /* suppress cursor-follow while spinning to / holding the front pose,
     so the label truly faces the camera regardless of mouse position */
  const wantInfluence = (spinTween || frontHold > 0) ? 0 : 1;
  ptrInfluence = lerp(ptrInfluence, wantInfluence, 0.1);
  spinGroup.rotation.y = spinY + pointer.x * POINTER_SPIN * ptrInfluence;

  /* bob all wrappers — invisible ones stay in sync so phase is correct on reveal */
  wrappers.forEach((w, i) => {
    w.position.y = Math.sin(elapsed * BOB_SPEED + (BOB_PHASES[i] || 0)) * BOB_AMP;
  });

  tiltGroup.rotation.x = clamp(-pointer.y * 0.45, -MAX_TILT, MAX_TILT);
  tiltGroup.rotation.z = BASE_TILT_Z + clamp(pointer.x * 0.22, -MAX_TILT, MAX_TILT);

  renderer.render(scene, camera);
}

/* ── load models (allSettled so one 404 doesn't kill everything) ── */
const loader = new GLTFLoader();
Promise.allSettled(MODEL_URLS.map(url => loader.loadAsync(url)))
  .then(results => {
    /* Build sparse array — null where a GLB failed to load */
    models = results.map(r => r.status === 'fulfilled' ? r.value.scene : null);

    if (!models.some(Boolean)) {
      console.error('eyn-stick-3d: no models loaded');
      root.classList.add('is-error');
      return;
    }

    activeIdx = models.findIndex(m => m !== null);

    models.forEach((m, i) => {
      if (!m) return;
      m.traverse(n => { if (n.isMesh && n.material) n.material.envMapIntensity = 1.0; });
      fitModel(m, STICK_HEIGHT);

      const w = new THREE.Group();
      w.add(m);
      spinGroup.add(w);
      wrappers.push(w);

      /* only the active flavor rendered; others culled by visible=false */
      m.visible = (i === activeIdx);
    });

    const loadedCount = models.filter(Boolean).length;
    const hasNav = loadedCount > 1;

    if (prevBtn) prevBtn.hidden = !hasNav;
    if (nextBtn) nextBtn.hidden = !hasNav;
    /* hide dots for any flavor whose GLB failed */
    section.querySelectorAll('[data-eyn-dot]').forEach((dot, i) => {
      if (!models[i]) dot.hidden = true;
    });
    const dotsEl = section.querySelector('.eyn-dots');
    if (dotsEl) dotsEl.hidden = !hasNav;

    root.classList.add('is-ready');
    updateDots(activeIdx);
    animate();
  });
